import { ssoApi } from "@sso/apis";
import { ApiMethodKeys, notificationsMsgs, SnackbarKeys } from "@sso/constant";
import { pushSnackbar } from "@sso/store/slices/snackbar";
import { LicencedSoftware, ProductToggleRoleRequest } from "@sso/types";

import { CacheKeys } from "../cacheKeys";
import { Endpoints } from "../endpoints";

export const userProductsApi = ssoApi
  .enhanceEndpoints({ addTagTypes: [CacheKeys.USER_PRODUCTS, CacheKeys.USER_ACCESSED_PRODUCTS, CacheKeys.USERS] })
  .injectEndpoints({
    endpoints: builder => ({
      userProducts: builder.query<LicencedSoftware[], void>({
        query: () => ({ url: `${Endpoints.USER_PRODUCTS}` }),
        transformResponse: (response: { result: LicencedSoftware[] }) => response.result,
        providesTags: [CacheKeys.USER_PRODUCTS],
      }),
      accessedByUser: builder.query<LicencedSoftware[], string>({
        query: userId => ({ url: `${Endpoints.USER_PRODUCTS}/AccessibleByUser`, params: { userId } }),
        transformResponse: (response: { result: LicencedSoftware[] }) => response.result,
        providesTags: [CacheKeys.USER_ACCESSED_PRODUCTS],
      }),
      toggleUserProductRole: builder.mutation<void, ProductToggleRoleRequest>({
        query: data => ({ url: `${Endpoints.USER_PRODUCTS}/ToggleRole`, method: ApiMethodKeys.POST, data }),
        invalidatesTags: [CacheKeys.USERS],
        onQueryStarted(request, { dispatch, queryFulfilled }) {
          queryFulfilled.then(() => {
            dispatch(pushSnackbar({ type: SnackbarKeys.SUCCESS, title: notificationsMsgs.USER_ROLES_UPDATED }));
            dispatch(
              userProductsApi.util.updateQueryData("accessedByUser", request.userId, draft => {
                return draft.map(licencedSoftware => {
                  if (licencedSoftware.softwareId === request.softwareId) {
                    const roles = licencedSoftware.assignedRoles.includes(request.roleId)
                      ? licencedSoftware.assignedRoles.filter(r => r !== request.roleId)
                      : licencedSoftware.assignedRoles.concat([request.roleId]);
                    return { ...licencedSoftware, assignedRoles: roles };
                  }
                  return { ...licencedSoftware };
                });
              }),
            );
          });
        },
      }),
    }),
  });

export const { useUserProductsQuery, useAccessedByUserQuery, useToggleUserProductRoleMutation } = userProductsApi;
